import { useMemo, useRef, useState, type ReactNode } from "react";
import { chartSegments } from "../chart-series";

/**
 * Small dependency-free SVG charts for the agency dashboard.
 *
 * All single-series and single-hue (the app's own ink), so no categorical
 * palette is involved. Each chart has a hover readout and an sr-only table, so
 * no value is available by pointer alone. Marks follow the house specs: 2px
 * lines, 4px rounded bar ends, recessive grid, no dual axes.
 */

type Point = { label: string; value: number };
type AreaPoint = { label: string; value: number | null };

// The axis top. Steps whose halves are round too (the midline is labelled):
// a $2.04K peak gets a $3K top and a $1.5K midline, not a $5K top over an
// empty chart (1, 2, 5 jumped straight from 2 to 5).
const STEPS = [1, 1.2, 1.5, 2, 3, 4, 5, 6, 8, 10];
const niceMax = (v: number) => {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  const n = v / p;
  return (STEPS.find((step) => n <= step + 1e-9) ?? 10) * p;
};

// The wrapper is what hides the table: a <table> ignores the 1px width and
// overflow of .sr-only, so on its own it stretched the page sideways.
function DataTable({ caption, rows, format }: { caption: string; rows: AreaPoint[]; format: (v: number) => string }) {
  return (
    <div className="sr-only">
      <table>
        <caption>{caption}</caption>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.label}-${i}`}>
              <th scope="row">{r.label}</th>
              <td>{r.value === null ? "Not available" : format(r.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ChartCard({
  eyebrow,
  title,
  meta,
  children,
  className = "",
}: {
  eyebrow: string;
  title: string;
  meta?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`panel chart-card ${className}`}>
      <div className="panel-heading">
        <div>
          <h2>{title}</h2>
        </div>
        {meta && <span className="panel-meta">{meta}</span>}
      </div>
      <div className="chart-body">{children}</div>
    </section>
  );
}

export function ChartEmpty({ children }: { children: ReactNode }) {
  // The span keeps the sentence one block: the grid made each text run and link its own row.
  return <p className="chart-empty"><span>{children}</span></p>;
}

/**
 * Where a point of the SVG sits inside `.chart-frame`, whose padding (44px
 * left for the y labels, 22px below for the x labels) the SVG does not cover.
 * A bare percentage of the frame would drift further off the line the further
 * right (or down) the point is.
 */
const FRAME_LEFT = 44;
const FRAME_BOTTOM = 22;
const frameX = (f: number) => `calc(${FRAME_LEFT}px + (100% - ${FRAME_LEFT}px) * ${f})`;
const frameY = (f: number) => `calc((100% - ${FRAME_BOTTOM}px) * ${f})`;

/** A trend over time with a crosshair readout. */
export function AreaChart({
  data,
  format,
  caption,
  height = 190,
  integer = false,
}: {
  data: AreaPoint[];
  format: (v: number) => string;
  caption: string;
  height?: number;
  /** Whole counts (leads, calls, visitors): the top is even, so the labelled midline is a whole number too. */
  integer?: boolean;
}) {
  const W = 640;
  const H = height;
  const pad = { t: 12, r: 8, b: 24, l: 8 };
  const [hover, setHover] = useState<number | null>(null);
  const ref = useRef<SVGSVGElement>(null);
  const peak = Math.max(...data.map((d) => d.value ?? 0), 0);
  const max = integer ? Math.max(2, 2 * Math.ceil(niceMax(peak) / 2)) : niceMax(peak);
  const x = (i: number) => pad.l + (data.length <= 1 ? 0 : (i / (data.length - 1)) * (W - pad.l - pad.r));
  const y = (v: number) => pad.t + (1 - v / max) * (H - pad.t - pad.b);
  const segments = chartSegments(data.map((d) => d.value));
  const paths = segments.map((segment) => {
    const line = segment.map((d, i) => `${i ? "L" : "M"}${x(d.index).toFixed(1)},${y(d.value).toFixed(1)}`).join("");
    return { line, area: `${line}L${x(segment[segment.length - 1].index)},${y(0)}L${x(segment[0].index)},${y(0)}Z` };
  });
  const ticks = [0, 0.5, 1].map((f) => max * f);
  const labelEvery = Math.max(1, Math.ceil(data.length / 6));

  const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const box = ref.current?.getBoundingClientRect();
    if (!box || !data.length) return;
    const px = ((e.clientX - box.left) / box.width) * W;
    const i = Math.round(((px - pad.l) / (W - pad.l - pad.r)) * (data.length - 1));
    setHover(Math.min(data.length - 1, Math.max(0, i)));
  };
  const h = hover !== null ? data[hover] : null;

  return (
    <div className="chart-frame">
      <svg
        ref={ref}
        viewBox={`0 0 ${W} ${H}`}
        className="chart-svg"
        role="img"
        aria-label={caption}
        onPointerMove={onMove}
        onPointerLeave={() => setHover(null)}
        preserveAspectRatio="none"
      >
        {ticks.map((t) => (
          <line key={t} className="chart-grid" x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} vectorEffect="non-scaling-stroke" />
        ))}
        {/* A flat, light fill under the line (no gradient wash). */}
        <path d={paths.map((p) => p.area).join("")} className="chart-area" />
        <path d={paths.map((p) => p.line).join("")} className="chart-line" vectorEffect="non-scaling-stroke" />
        {h && hover !== null && (
          <line className="chart-cross" x1={x(hover)} x2={x(hover)} y1={pad.t} y2={y(0)} vectorEffect="non-scaling-stroke" />
        )}
      </svg>
      {h && hover !== null && (
        <>
          {h.value !== null && <span className="chart-dot" style={{ left: frameX(x(hover) / W), top: frameY(y(h.value) / H) }} />}
          <div
            className="chart-tip"
            style={{ left: frameX(x(hover) / W), transform: `translateX(${hover > data.length / 2 ? "-105%" : "5%"})` }}
          >
            <strong>{h.value === null ? "Not available" : format(h.value)}</strong>
            <span>{h.label}</span>
          </div>
        </>
      )}
      <div className="chart-y-labels" aria-hidden="true">
        {ticks.slice(1).reverse().map((t) => (
          <span key={t} style={{ top: frameY(y(t) / H) }}>{format(t)}</span>
        ))}
      </div>
      <div className="chart-x-labels" aria-hidden="true">
        {data.map((d, i) =>
          (i % labelEvery === 0 && data.length - 1 - i >= labelEvery * 0.6) || i === data.length - 1 ? (
            <span key={d.label} style={{ left: `${(x(i) / W) * 100}%` }}>{d.label}</span>
          ) : null,
        )}
      </div>
      <DataTable caption={caption} rows={data} format={format} />
    </div>
  );
}

/**
 * Ranked magnitudes as horizontal bars, value labelled at the end. `wrap` lets a long
 * label run onto more lines instead of ending in an ellipsis, for names that differ
 * only at the end (Google's conversion actions).
 */
export function BarList({
  data,
  format,
  caption,
  max: fixedMax,
  limit = 6,
  wrap = false,
}: {
  data: Point[];
  format: (v: number) => string;
  caption: string;
  max?: number;
  limit?: number;
  wrap?: boolean;
}) {
  const rows = data.slice(0, limit);
  const max = fixedMax ?? Math.max(...rows.map((r) => r.value), 1);
  // The bars are for the eye; screen readers get the table beside them (inside a
  // role="img" wrapper a table is hidden from them).
  return (
    <>
    <div className={wrap ? "chart-bars chart-bars-wrap" : "chart-bars"} aria-hidden="true">
      {rows.map((r, i) => (
        <div className="chart-bar-row" key={`${r.label}-${i}`} title={`${r.label}: ${format(r.value)}`}>
          <div className="chart-bar-label">
            <span>{r.label}</span>
            <strong>{format(r.value)}</strong>
          </div>
          <div className="chart-bar-track">
            <span style={{ width: `${Math.max((r.value / max) * 100, r.value > 0 ? 2 : 0)}%` }} />
          </div>
        </div>
      ))}
    </div>
    <DataTable caption={caption} rows={rows} format={format} />
    </>
  );
}

/** Counts per category as vertical columns (a distribution, not a ranking). */
export function ColumnChart({
  data,
  format = (v) => String(v),
  caption,
  height = 150,
}: {
  data: Point[];
  format?: (v: number) => string;
  caption: string;
  height?: number;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const max = useMemo(() => niceMax(Math.max(...data.map((d) => d.value), 0)), [data]);
  // Many narrow columns: label every Nth one (and the last), each label free to
  // run wider than its column, instead of cutting every label to "S…".
  const labelEvery = Math.max(1, Math.ceil(data.length / 7));
  const n = data.length;
  const showLabel = (i: number) => i === n - 1 || (i % labelEvery === 0 && n - 1 - i >= labelEvery);
  return (
    <>
    <div className={`chart-columns${labelEvery > 1 ? " sparse-labels" : ""}`} aria-hidden="true" style={{ height }}>
      {data.map((d, i) => (
        <div
          className="chart-col"
          key={d.label}
          onPointerEnter={() => setHover(i)}
          onPointerLeave={() => setHover(null)}
          data-hover={hover === i}
        >
          <span className="chart-col-value">{format(d.value)}</span>
          <div className="chart-col-track">
            <span style={{ height: `${d.value ? Math.max((d.value / max) * 100, 3) : 0}%` }} />
          </div>
          <span className="chart-col-label">{showLabel(i) ? d.label : "\u00a0"}</span>
        </div>
      ))}
    </div>
    <DataTable caption={caption} rows={data} format={format} />
    </>
  );
}

/** A tiny trend line for a table cell: no axes, no hover; the label carries the numbers. */
export function Sparkline({ values, label, width = 72, height = 20 }: { values: number[]; label: string; width?: number; height?: number }) {
  if (values.length < 2) return <span className="muted">—</span>;
  const max = Math.max(...values, 1);
  const x = (i: number) => 1 + (i / (values.length - 1)) * (width - 2);
  const y = (v: number) => 1 + (1 - v / max) * (height - 2);
  const d = values.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join("");
  return (
    <svg className="sparkline" width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label}>
      <path d={d} className="chart-line" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
